import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AuthenticationGuard from '../../components/ui/AuthenticationGuard';
import SmartLoadingBuffer from '../../components/ui/SmartLoadingBuffer';
import DailyProgressSummary from './components/DailyProgressSummary';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    // Simulate loading dashboard data
    const loadDashboard = async () => {
      try {
        // Simulate API calls for dashboard data
        await new Promise(resolve => setTimeout(resolve, 500));
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading dashboard:', error);
        setIsLoading(false);
      }
    };

    loadDashboard();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const onboardingComplete =
        typeof window !== 'undefined' &&
        window.localStorage.getItem('cogito_getting_started_complete') === 'true';
      setShowWelcome(!onboardingComplete);
    }
  }, [isLoading]);

  const handleDismissWelcome = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('cogito_getting_started_complete', 'true');
    }
    setShowWelcome(false);
  }, []);

  if (isLoading) {
    return (
      <AuthenticationGuard isAuthenticated={true}>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <SmartLoadingBuffer
              className="min-h-[60vh]"
              title="Preparing your dashboard"
              message="We’re gathering your habits, knowledge, and progress so you can kick off the day."
              hint="Cogito is aligning your learning signals."
            />
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
            {/* Welcome Section */}
            {showWelcome && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Sparkles" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-foreground">Welcome back!</h1>
                      <p className="text-sm text-muted-foreground">
                        Follow the quick-start checklist or log a habit to unlock personalized insights.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                    <Button
                      variant="default"
                      iconName="Compass"
                      onClick={() => navigate('/getting-started')}
                      className="sm:w-auto w-full"
                    >
                      Getting Started Guide
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Target"
                      onClick={() => navigate('/habits')}
                      className="sm:w-auto w-full"
                    >
                      Add a Habit
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Check"
                    onClick={handleDismissWelcome}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    I’ve completed the setup
                  </Button>
                </div>
              </div>
            )}

            {/* Progress Snapshot */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">This week at a glance</h2>
                  <p className="text-sm text-muted-foreground">Real-time stats will appear once your habits and decks have activity.</p>
                </div>
                <Icon name="BarChart3" size={20} className="text-primary" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-1">
                  <span className="text-xs uppercase text-muted-foreground tracking-wide">Habit streak</span>
                  <p className="text-2xl font-semibold text-foreground">0 days</p>
                  <p className="text-xs text-muted-foreground">Keep logging to build momentum.</p>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-1">
                  <span className="text-xs uppercase text-muted-foreground tracking-wide">Flashcards reviewed</span>
                  <p className="text-2xl font-semibold text-foreground">0 cards</p>
                  <p className="text-xs text-muted-foreground">Create a deck to see progress here.</p>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-1">
                  <span className="text-xs uppercase text-muted-foreground tracking-wide">Learning time</span>
                  <p className="text-2xl font-semibold text-foreground">0 min</p>
                  <p className="text-xs text-muted-foreground">Add habits with durations to track it.</p>
                </div>
              </div>
            </div>

            {/* Main Content - Simple Stack Layout */}
            <div className="space-y-6">
              {/* Daily Progress Summary */}
              <DailyProgressSummary />
              
              {/* Quick Actions */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <button
                    onClick={() => navigate('/habits')}
                    className="flex flex-col items-center p-4 bg-muted/20 rounded-lg hover:bg-muted/40 transition-colors"
                  >
                    <div className="w-8 h-8 bg-success/20 rounded-lg flex items-center justify-center mb-2">
                      <Icon name="Target" size={18} className="text-success" />
                    </div>
                    <span className="text-sm font-medium text-foreground text-center">Habits</span>
                  </button>
                  
                  <button
                    onClick={() => navigate('/flashcards')}
                    className="flex flex-col items-center p-4 bg-muted/20 rounded-lg hover:bg-muted/40 transition-colors"
                  >
                    <div className="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center mb-2">
                      <Icon name="Brain" size={18} className="text-accent" />
                    </div>
                    <span className="text-sm font-medium text-foreground text-center">Flashcards</span>
                  </button>
                  
                  <button
                    onClick={() => navigate('/knowledge')}
                    className="flex flex-col items-center p-4 bg-muted/20 rounded-lg hover:bg-muted/40 transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center mb-2">
                      <Icon name="BookOpen" size={18} className="text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground text-center">Knowledge</span>
                  </button>
                  
                  <button
                    onClick={() => navigate('/progress')}
                    className="flex flex-col items-center p-4 bg-muted/20 rounded-lg hover:bg-muted/40 transition-colors"
                  >
                    <div className="w-8 h-8 bg-warning/20 rounded-lg flex items-center justify-center mb-2">
                      <Icon name="TrendingUp" size={18} className="text-warning" />
                    </div>
                    <span className="text-sm font-medium text-foreground text-center">Progress</span>
                  </button>
                </div>
              </div>

              {/* Feature Highlights */}
              <div className="bg-muted/20 border border-dashed border-border rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Bot" size={18} className="text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground">Try the AI assistant</h3>
                    <p className="text-sm text-muted-foreground">Copy starter prompts or share ideas for how AI can support your learning routine.</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Sparkles"
                      className="justify-start px-0"
                      onClick={() => navigate('/assistant')}
                    >
                      Visit AI Assistant
                    </Button>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="FlaskConical" size={18} className="text-accent" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground">QA workspace updates</h3>
                    <p className="text-sm text-muted-foreground">Follow our testing roadmap and try the manual checks while automated suites are coming together.</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="ExternalLink"
                      className="justify-start px-0"
                      onClick={() => navigate('/testing')}
                    >
                      Open Testing Workspace
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default Dashboard;